import { ConfigProvider } from 'antd';
import DeviceMaintenanceRoute from 'app/routes/DeviceMaintenance.route';
import ExacqVisionVMSRoute from 'app/routes/ExacqVisionVMS.route';
import IntegrationsRoute from 'app/routes/Integrations.route';
import AlarmListener from 'components/AlarmListener';
import MobileHeader from 'components/Header/MobileHeader';
import MobileNav from 'components/Nav/MobileNav';
import ThemeSelectorTool from 'components/ThemeSelectorTool';
import { PermsProvider, usePerms } from 'contexts/PermsContext';
import { GetRouteData } from 'data/routeData';
import { AuthContext } from 'features/Auth/contexts/AuthContext';
import DeviceContextProvider from 'features/integrations/contexts/DeviceContextProvider';
import DeviceMaintenance from 'features/integrations/features/device-maintenance/DeviceMaintenance.index';
import Cookies from 'js-cookie';
import { Page404 } from 'pages/404';
import AuditLogs from 'pages/Admin/AuditLogs';
import { ForgotPassword } from 'pages/ForgotPassword';
import PolicyAndCookies from 'pages/Privacy/PolicyAndCookies';
import { PrivacyPopup } from 'pages/Privacy/PrivacyPopup';
import Redirect from 'pages/Redirect';
import ResetPassword from 'pages/ResetPassword';
import ResetPasswordRedirect from 'pages/ResetPasswordRedirect';
import TermsAndConditions from 'pages/TermsAndConditions';
import UserMenu from 'pages/UserMenu/UserMenu';
import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { PiVideoFill } from 'react-icons/pi';
import { Route, BrowserRouter as Router, Routes } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { logout } from 'services/auth/auth.service';
import { getAccessLevel } from 'services/perms/perms.service';
import { getPolicyAndCookies, getTermsAndConditions } from 'services/policy/policy.service';
import { clearCookies } from 'utils/cookieUtils';
import { themes } from 'utils/themeUtils';
import { addViewportResizeListeners } from 'utils/viewportResizeUtils';
import './App.css';
import Header from './components/Header/Header';
import Sidebar from './components/Nav/Sidebar';
import GetWidgetData from './data/widgetData/widgetData.js';
import ActivateAccount from './pages/ActivateAccount';
import Dashboard from './pages/Dashboard/Dashboard';
import Signin from './pages/Signin';
import Signup from './pages/Signup';
import AccessibilitySettings from './pages/UserMenu/AccessibilitySettings';

// Dev-only overlay is lazy-loaded so it does not get bundled in production builds
let DevRenderOverlay = null;
const _isDevEnv = process.env.NODE_ENV === 'development' || false;
console.log('Environment:', process.env.NODE_ENV);
if (_isDevEnv) {
  const LazyDevRenderOverlay = React.lazy(
    () => import('./components/DevRenderOverlay/DevRenderOverlay.jsx'),
  );
  DevRenderOverlay = (props) => (
    <React.Suspense fallback={null}>
      <LazyDevRenderOverlay {...props} />
    </React.Suspense>
  );
}

export const PageContext = createContext();
export const SidebarContext = createContext(true);
export const ThemesListContext = createContext([]);
export const MicrosoftContext = createContext();

const baseSize = 1000;
export const LayoutSizeContext = createContext({
  width: baseSize,
  height: window.innerHeight - 137,
  widgetWidth: baseSize / 3,
  doubleWidgetWidth: (baseSize / 3) * 2 + 9,
  widgetHeight: baseSize / 3,
  doubleWidgetHeight: baseSize / 3 + 100,
  scaleMult: 1,
  baseFontSize: 14,
});

//how many colors do we need?
//are there any colors that serve a specific purpose (ie. main background color (that 'dark-grey' color behind all of the pages/widgets))?
export const UniqueWidgetRefreshContext = createContext({
  base: 0,
  primary: 0,
  secondary: 0,
  tertiary: 0,
  fontColor: 'white',
  fontColor: 'white',
});

export const SetUniqueWidgetRefreshContext = createContext(() => {});
export const ColorContext = createContext({});
export const SetColorContext = createContext(() => {});
export const PermissionContext = createContext({});
export const UserAgentContext = createContext({});

function App() {
  // Dev-only render tracking
  try {
    if (process.env.NODE_ENV === 'development') {
      require('hooks/useRenderTracker').default('App');
    }
  } catch (e) {}

  const authContext = useContext(AuthContext);
  const { accessToken } = authContext;

  const [selectedTheme, setSelectedTheme] = useState(themes[0]);

  const [colors, setColors] = useState({
    ...themes[0].value,
  });
  //change colors in react and css
  useEffect(() => {
    document.body.dataset.theme = selectedTheme.tName;
  }, [selectedTheme]);

  const [microsoftContextValue, setMicrosoftContextValue] = useState({
    hasClientId: false,
  });
  const [dimensions, setDimensions] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });
  const [showFull, setShowFull] = useState(() => {
    const cookiesAccepted = Cookies.get('cookiesAgreed') === 'true';
    const privacyAccepted = Cookies.get('privacyAgreed') === 'true';
    return !(cookiesAccepted && privacyAccepted);
  });
  const [updateSidebar] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [routeName, setRouteName] = useState({
    name: 'dashboard',
    displayName: 'Home Dashboard',
  });
  const [accessLevel, setAccessLevel] = useState(-1);
  const [search, setSearch] = useState('');
  const [widgetRefresh, setWidgetRefresh] = useState('');
  const [cookiesAgreed, setCookiesAgreed] = useState(() => Cookies.get('cookiesAgreed') === 'true');
  const [privacyAgreed, setPrivacyAgreed] = useState(() => Cookies.get('privacyAgreed') === 'true');
  const [termsAgreed, setTermsAgreed] = useState(false);
  const [newTermsFullyAgreed, setNewTermsFullyAgreed] = useState(false);
  const [hasSignedIn, setHasSignedIn] = useState(false);
  const [pagesLoaded, setPagesLoaded] = useState(false);
  const [termsFullyViewed, setTermsFullyViewed] = useState(false);
  const [termsData, setTermsData] = useState({ version: '', page: '' });
  const [policyData, setPolicyData] = useState({ version: '', page: '' });
  const [showSmallMenu, setShowSmallMenu] = useState(true);
  const hasAcceptedPolicy = privacyAgreed && cookiesAgreed;

  useEffect(() => {
    const fetchTermsAndConditions = async () => {
      try {
        const termsData = await getTermsAndConditions();
        if (termsData.status !== 200) {
          // alert("Unable to connect to the server. Please try again later.");

          return;
        }
        setTermsData(termsData.data);
      } catch (error) {
        console.error('Error fetching terms and conditions:', error);
      }
    };

    const fetchPolicyAndCookies = async () => {
      try {
        const policyData = await getPolicyAndCookies();
        if (policyData?.status !== 200) {
          // alert("Unable to connect to the server. Please try again later.");

          return;
        }
        setPolicyData(policyData.data);
      } catch (error) {
        // alert("Unable to connect to the server. Please try again later.");
        console.error('Error fetching policy and cookies:', error);
      }
    };

    fetchTermsAndConditions();
    fetchPolicyAndCookies();
  }, []);

  useEffect(() => {
    if (!policyData?.version) return;

    const acceptedVersion = Cookies.get('policyAgreed');
    if (acceptedVersion === policyData.version) {
      if (!cookiesAgreed || !privacyAgreed) {
        setCookiesAgreed(true);
        setPrivacyAgreed(true);
      }
      Cookies.set('cookiesAgreed', 'true', { expires: 365 });
      Cookies.set('privacyAgreed', 'true', { expires: 365 });
      return;
    }

    if (hasAcceptedPolicy) {
      Cookies.set('policyAgreed', policyData.version, { expires: 365 });
      Cookies.set('cookiesAgreed', 'true', { expires: 365 });
      Cookies.set('privacyAgreed', 'true', { expires: 365 });
      return;
    }

    Cookies.remove('policyAgreed');
    Cookies.remove('cookiesAgreed');
    Cookies.remove('privacyAgreed');
    if (cookiesAgreed || privacyAgreed) {
      setCookiesAgreed(false);
      setPrivacyAgreed(false);
    }
  }, [policyData?.version, hasAcceptedPolicy, cookiesAgreed, privacyAgreed]);

  const [showThemeSelector, setShowThemeSelector] = useState(false);

  const divStyle = {
    color: colors.fontColor,
    width: '100%',
  };

  const initialWidth = Math.max(window.innerWidth - (sidebarOpen ? 295 : 100), 1000);

  const [layoutObject, setLayoutObject] = useState({
    width: initialWidth,
    height: window.innerHeight - 137,
    widgetWidth: initialWidth / 3,
    doubleWidgetWidth: (initialWidth / 3) * 2 + 9,
    widgetHeight: initialWidth / 3,
    doubleWidgetHeight: initialWidth / 3 + 100,
    scaleMult: 1,
    baseFontSize: 14,
  });

  const [uwfValues, setUwfValues] = useState({
    base: 0,
  });

  const lastLayoutRef = useRef(null);
  const lastDimensionsRef = useRef(null);

  useEffect(() => {
    const handleResize = () => {
      const newSize = Math.max(window.innerWidth - (sidebarOpen ? 295 : 100), 1000);

      const newSizeHeight = window.innerHeight - 107;

      const newLayoutObject = {
        width: newSize * 0.9875,
        height: newSizeHeight,
        widgetWidth: newSize / 3,
        doubleWidgetWidth: (newSize / 3) * 2 + 9,
        widgetHeight: newSize / 3,
        doubleWidgetHeight: (newSize / 3) * 2 + 9,
        scaleMult: newSize / baseSize,
        baseFontSize: Math.min(Math.max(((16 * newSize) / baseSize) * 0.75, 14), 30),
      };

      const newDimensions = {
        width: window.innerWidth,
        height: window.innerHeight,
      };

      // Only update if values actually changed
      if (JSON.stringify(lastLayoutRef.current) !== JSON.stringify(newLayoutObject)) {
        lastLayoutRef.current = newLayoutObject;
        setLayoutObject(newLayoutObject);
      }

      if (JSON.stringify(lastDimensionsRef.current) !== JSON.stringify(newDimensions)) {
        lastDimensionsRef.current = newDimensions;
        setDimensions(newDimensions);
      }
    };
    const removeViewportResizeListeners = addViewportResizeListeners(handleResize);

    handleResize();
    return () => {
      removeViewportResizeListeners();
    };
  }, [sidebarOpen]);

  return (
    <ContextProvider
      colors={colors}
      sidebarOpen={sidebarOpen}
      layoutObject={layoutObject}
      microsoftContextValue={microsoftContextValue}
      setMicrosoftContextValue={setMicrosoftContextValue}
      uwfValues={uwfValues}
      setUwfValues={setUwfValues}
      themes={themes}
      setColors={setColors}
      accessToken={accessToken}
    >
      <AppRoutingChildren
        colors={colors}
        selectedTheme={selectedTheme}
        setSelectedTheme={setSelectedTheme}
        dimensions={dimensions}
        showFull={showFull}
        setShowFull={setShowFull}
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        mobileNavOpen={mobileNavOpen}
        setMobileNavOpen={setMobileNavOpen}
        routeName={routeName}
        setRouteName={setRouteName}
        accessLevel={accessLevel}
        setAccessLevel={setAccessLevel}
        search={search}
        setSearch={setSearch}
        widgetRefresh={widgetRefresh}
        setWidgetRefresh={setWidgetRefresh}
        cookiesAgreed={cookiesAgreed}
        setCookiesAgreed={setCookiesAgreed}
        privacyAgreed={privacyAgreed}
        setPrivacyAgreed={setPrivacyAgreed}
        termsAgreed={termsAgreed}
        setTermsAgreed={setTermsAgreed}
        newTermsFullyAgreed={newTermsFullyAgreed}
        setNewTermsFullyAgreed={setNewTermsFullyAgreed}
        hasSignedIn={hasSignedIn}
        setHasSignedIn={setHasSignedIn}
        termsFullyViewed={termsFullyViewed}
        setTermsFullyViewed={setTermsFullyViewed}
        termsData={termsData}
        policyData={policyData}
        showSmallMenu={showSmallMenu}
        setShowSmallMenu={setShowSmallMenu}
        hasAcceptedPolicy={hasAcceptedPolicy}
        showThemeSelector={showThemeSelector}
        setShowThemeSelector={setShowThemeSelector}
        divStyle={divStyle}
        updateSidebar={updateSidebar}
      />
    </ContextProvider>
  );
}

export default App;

const AppRoutingChildren = ({
  colors,
  selectedTheme,
  setSelectedTheme,
  dimensions,
  showFull,
  setShowFull,
  sidebarOpen,
  setSidebarOpen,
  mobileNavOpen,
  setMobileNavOpen,
  routeName,
  setRouteName,
  accessLevel,
  setAccessLevel,
  search,
  setSearch,
  widgetRefresh,
  setWidgetRefresh,
  cookiesAgreed,
  setCookiesAgreed,
  privacyAgreed,
  setPrivacyAgreed,
  termsAgreed,
  setTermsAgreed,
  newTermsFullyAgreed,
  setNewTermsFullyAgreed,
  hasSignedIn,
  setHasSignedIn,
  termsFullyViewed,
  setTermsFullyViewed,
  termsData,
  policyData,
  showSmallMenu,
  setShowSmallMenu,
  hasAcceptedPolicy,
  showThemeSelector,
  setShowThemeSelector,
  divStyle,
  updateSidebar,
}) => {
  // Get access to perms context
  const perms = usePerms();

  // State that depends on perms context
  const [routes, setRoutes] = useState([]);
  const [pagesLoaded, setPagesLoaded] = useState(false);
  const [hasFetchedPerms, setHasFetchedPerms] = useState(false);
  const authContext = useContext(AuthContext);
  const { isAuthenticated, isInitialized, logout: authLogout } = authContext;

  // Use auth context's initialization state instead of separate loading
  const isAuthLoading = !isInitialized;

  useEffect(() => {
    // Only fetch permissions when authenticated
    if (!isAuthenticated) {
      perms.reset();
      return;
    }

    const fetchPermsAndLevel = async () => {
      try {
        const res = await getAccessLevel();
        if (res.status === 200) {
          const result = res?.data?.result;
          // Accept falsy values like 0 or false — only reject null/undefined
          if (
            result &&
            result.group_id != null &&
            result.maintenanceOperatorLevel != null &&
            result.serviceManager !== undefined
          ) {
            perms.setAccessLevel(result.group_id);
            perms.setMaintenanceLevel(result.maintenanceOperatorLevel);
            perms.setServiceManagerFlag(result.serviceManager);
            setAccessLevel(result.group_id);
            perms.get();
          } else {
            throw new Error(
              `Missing parameter in fetchPermsAndLevel response: ${
                result?.group_id ?? 'undefined'
              } || ${result?.maintenanceOperatorLevel ?? 'undefined'} || ${
                result?.serviceManager ?? 'undefined'
              }`,
            );
          }
        }
      } catch (error) {
        console.error('Error fetching access level:', error);
        perms.reset();
        // Auth context will handle logout via interceptor
      }
    };

    if (!hasFetchedPerms) {
      fetchPermsAndLevel();
      setHasFetchedPerms(true);
    }
  }, [isAuthenticated, hasFetchedPerms]);

  // Effects that depend on perms context
  useEffect(() => {
    const cookie = perms.get();
    if (!cookie || !isAuthenticated) {
      return;
    }
    let allowed = [];

    const appdata = [...GetRouteData(perms, widgetRefresh)];

    appdata.forEach((item) => {
      if (item.permission()) {
        item.options.forEach((option) => {
          //set enabled by perm
          if (option.permission()) {
            allowed.push(option);
          }
        });
      }
    });
    //This is a bandaid Fix, do not reuse
    try {
      if (
        perms.testPermission([8, 9, 10, 11, 12, 13], ['ViewExacqVisionOverwatchVMSConnections'])
      ) {
        allowed.push({
          name: 'Overwatch VMS',
          icon: () => {
            return <PiVideoFill />;
          },
          enabled: true,
          href: '/vms',
          permission: () =>
            perms.testPermission(
              [8, 9, 10, 11, 12, 13],
              ['ViewExacqVisionOverwatchVMSConnections'],
            ),
          page: <ExacqVisionVMSRoute />,
        });
      }

      if (perms.maintenanceLevel > 0) {
        allowed.push({
          name: 'Maintenance Devices',
          icon: () => {
            return;
          },
          enabled: true,
          href: '/maintenancedashboard',
          permission: () => perms.maintenanceLevel > 0,
          page: <DeviceMaintenance />,
        });
      }
    } catch (error) {
      console.error('Error setting routes based on permissions:', error);
    }

    setRoutes(allowed);
    setPagesLoaded(true);
  }, [perms.data, perms.maintenanceLevel, updateSidebar, isAuthenticated, widgetRefresh]);

  useEffect(() => {
    if (!isAuthenticated) perms.reset();
    if (isAuthenticated) setShowFull(false);
  }, [isAuthenticated]);

  return (
    <>
      {process.env.NODE_ENV === 'development' && showThemeSelector && (
        <ThemeSelectorTool
          setSelectedTheme={setSelectedTheme}
          setShowThemeSelector={setShowThemeSelector}
        />
      )}
      {isAuthLoading || (isAuthenticated && !pagesLoaded) ? (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100vw',
            height: '100vh',
            backgroundColor: colors.background,
            color: colors.fontColor,
          }}
        >
          <div className="text-center">
            <div className="spinner-border" role="status" style={{ width: '3rem', height: '3rem' }}>
              <span className="visually-hidden">Loading...</span>
            </div>
            <div className="mt-3">
              {isAuthLoading ? 'Authenticating...' : 'Loading application...'}
            </div>
          </div>
        </div>
      ) : (
        <>
          {/**
           * Router and Page Structure
           * v7_startTransition: true - enables concurrent features for route transitions
           * v7_relativeSplatPath: true - enables relative splat paths for nested routes
           */}
          <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                backgroundColor: colors.background,
                backgroundSize: 'cover',
                backgroundRepeat: 'repeat',
                width: '100vw',
                height: '100vh',
                flex: 1,
                flexGrow: 1,
              }}
            >
              {perms.testPermission([8, 9, 10, 11, 12, 13], ['PendingAlarms']) &&
                isAuthenticated && <AlarmListener />}
              {isAuthenticated && dimensions.width > 992 ? (
                <Sidebar
                  onOpenChanged={setSidebarOpen}
                  routeName={routeName}
                  accessLevel={accessLevel}
                  updateSidebar={updateSidebar}
                  definedWidgetData={GetWidgetData(widgetRefresh, setWidgetRefresh, sidebarOpen)}
                />
              ) : (
                <MobileNav isOpen={setMobileNavOpen} open={mobileNavOpen} />
              )}

              <div
                id="main-scroll-container"
                style={{
                  width: '100%',
                  overflowY: 'auto',
                  overflowX: 'hidden',
                }}
              >
                {showSmallMenu && (
                  <PrivacyPopup
                    hasAcceptedPolicy={hasAcceptedPolicy}
                    setShowFull={setShowFull}
                    showFull={showFull}
                    setMobileNavOpen={setMobileNavOpen}
                    acceptAction={() => {
                      setCookiesAgreed(true);
                      setPrivacyAgreed(true);
                      if (policyData?.version) {
                        Cookies.set('policyAgreed', policyData.version, {
                          expires: 365,
                        });
                      }
                      Cookies.set('cookiesAgreed', 'true', { expires: 365 });
                      Cookies.set('privacyAgreed', 'true', { expires: 365 });
                    }}
                    declineAction={() => {
                      if (isAuthenticated) {
                        logout();
                        authLogout();
                      }
                      setHasFetchedPerms(false);
                      setCookiesAgreed(false);
                      Cookies.remove('cookiesAgreed');
                      setPrivacyAgreed(false);
                      Cookies.remove('privacyAgreed');
                      Cookies.remove('policyAgreed');
                      setNewTermsFullyAgreed(false);
                      setTermsFullyViewed(false);
                      setHasSignedIn(false);
                      clearCookies();
                      // setMicrosoftButtonPressed(false);
                    }}
                  />
                )}
                {isAuthenticated && dimensions.width > 992 ? (
                  <Header
                    setHasSignedIn={setHasSignedIn}
                    routeName={routeName}
                    widgetRefresh={widgetRefresh}
                    setWidgetRefresh={setWidgetRefresh}
                    setShowFull={setShowFull}
                    setNewTermsFullyAgreed={setNewTermsFullyAgreed}
                    setTermsFullyViewed={setTermsFullyViewed}
                    setHasFetchedPerms={setHasFetchedPerms}
                    // created setCookiesAgreed and setPrivacyAgreed props for Header to use on logout
                    setCookiesAgreed={setCookiesAgreed}
                    setPrivacyAgreed={setPrivacyAgreed}
                  />
                ) : (
                  isAuthenticated && (
                    <MobileHeader
                      isMobileNavOpen={setMobileNavOpen}
                      setHasSignedIn={setHasSignedIn}
                      setShowFull={setShowFull}
                      setNewTermsFullyAgreed={setNewTermsFullyAgreed}
                      setTermsFullyViewed={setTermsFullyViewed}
                      // created setCookiesAgreed and setPrivacyAgreed props for MobileHeader to use on logout
                      setCookiesAgreed={setCookiesAgreed}
                      setPrivacyAgreed={setPrivacyAgreed}
                      setHasFetchedPerms={setHasFetchedPerms}
                    />
                  )
                )}
                <div className="main-content-area p-0 p-md-3">
                  <Routes>
                    {/* Redirect to login */}
                    {isAuthenticated === false && (
                      <>
                        <Route path="*" element={<Redirect route={'/'} />} />
                        <Route
                          path="/"
                          element={
                            <Signin
                              setShowFull={setShowFull}
                              hasSignedIn={hasSignedIn}
                              setHasSignedIn={setHasSignedIn}
                              widgetRefresh={widgetRefresh}
                              setCookiesAgreed={setCookiesAgreed}
                              setPrivacyAgreed={setPrivacyAgreed}
                              setTermsAgreed={setTermsAgreed}
                              setNewTermsFullyAgreed={setNewTermsFullyAgreed}
                              newTermsFullyAgreed={newTermsFullyAgreed}
                              cookiesAgreed={cookiesAgreed}
                              privacyAgreed={privacyAgreed}
                              termsAgreed={termsAgreed}
                              termsVersion={termsData?.version}
                              policyVersion={policyData?.version}
                              termsDate={termsData?.date}
                              terms={termsData?.page}
                              termsFullyViewed={termsFullyViewed}
                              setTermsFullyViewed={setTermsFullyViewed}
                            />
                          }
                        />
                        <Route
                          path="/activate"
                          element={
                            <PageContentWrapper>
                              <ActivateAccount
                                setCookiesAgreed={setCookiesAgreed}
                                setPrivacyAgreed={setPrivacyAgreed}
                              />
                            </PageContentWrapper>
                          }
                        />
                        <Route
                          path="/resetPassword"
                          element={
                            <PageContentWrapper>
                              <ResetPassword
                                setCookiesAgreed={setCookiesAgreed}
                                setPrivacyAgreed={setPrivacyAgreed}
                                setShowSmallMenu={setShowSmallMenu}
                              />
                            </PageContentWrapper>
                          }
                        />
                        <Route
                          path="/forgotPassword"
                          element={
                            <PageContentWrapper>
                              <ForgotPassword setShowSmallMenu={setShowSmallMenu} />
                            </PageContentWrapper>
                          }
                        />
                        <Route
                          path="/passwordRedirect"
                          element={
                            <PageContentWrapper>
                              <ResetPasswordRedirect />
                            </PageContentWrapper>
                          }
                        />
                        <Route
                          path="/terms"
                          element={
                            <PageContentWrapper>
                              <TermsAndConditions
                                data={termsData}
                                setShowFull={setShowFull}
                                setShowSmallMenu={setShowSmallMenu}
                              />
                            </PageContentWrapper>
                          }
                        />
                        <Route
                          path="/policy"
                          element={
                            <PageContentWrapper allowPageScroll>
                              <PolicyAndCookies
                                data={policyData}
                                setShowFull={setShowFull}
                                setShowSmallMenu={setShowSmallMenu}
                              />
                            </PageContentWrapper>
                          }
                        />
                      </>
                    )}
                    {isAuthenticated && (
                      <>
                        <Route path="/" element={<Redirect route={'/dashboard'} />} />
                        <Route
                          path="/activate"
                          element={
                            <PageContentWrapper>
                              <ActivateAccount
                                setCookiesAgreed={setCookiesAgreed}
                                setPrivacyAgreed={setPrivacyAgreed}
                              />
                            </PageContentWrapper>
                          }
                        />
                        <Route
                          path="/accessibility"
                          element={
                            <PageContentWrapper>
                              <AccessibilitySettings />
                            </PageContentWrapper>
                          }
                        />
                        <Route
                          path="/register"
                          element={
                            <PageContentWrapper>
                              <Signup />
                            </PageContentWrapper>
                          }
                        />
                        <Route
                          path="/dashboard"
                          element={
                            <div
                              style={{
                                ...divStyle,
                                overflow: 'auto',
                              }}
                              className="customScrollbar-X  customScrollbar-Y"
                              style={divStyle}
                              className="page-content-wrapper d-flex flex-column h-100 customScrollbar-X customScrollbar-Y"
                            >
                              <Dashboard
                                dimensions={dimensions}
                                sidebarOpen={sidebarOpen}
                                search={search}
                                definedWidgetData={GetWidgetData(
                                  widgetRefresh,
                                  setWidgetRefresh,
                                  sidebarOpen,
                                )}
                                accessLevel={accessLevel}
                              />
                            </div>
                          }
                        />
                        <Route
                          path="/terms"
                          element={
                            <PageContentWrapper>
                              <TermsAndConditions
                                data={termsData}
                                setShowFull={setShowFull}
                                setShowSmallMenu={setShowSmallMenu}
                              />
                            </PageContentWrapper>
                          }
                        />
                        <Route
                          path="/policy"
                          element={
                            <PageContentWrapper allowPageScroll>
                              <PolicyAndCookies
                                setShowFull={setShowFull}
                                setShowSmallMenu={setShowSmallMenu}
                                data={policyData}
                              />
                            </PageContentWrapper>
                          }
                        />

                        <Route
                          path="/usermenu"
                          element={
                            <PageContentWrapper>
                              <UserMenu />
                            </PageContentWrapper>
                          }
                        />

                        <Route
                          path="/system/auditlogs"
                          element={
                            <PageContentWrapper>
                              <AuditLogs />
                            </PageContentWrapper>
                          }
                        />

                        <Route
                          path="/system/integrations"
                          element={
                            <PageContentWrapper allowPageScroll>
                              <IntegrationsRoute />
                            </PageContentWrapper>
                          }
                        />

                        <Route
                          path="/system/devices"
                          element={
                            <PageContentWrapper allowPageScroll>
                              <IntegrationsRoute />
                            </PageContentWrapper>
                          }
                        />

                        {perms.testPermission(
                          [8, 9, 10, 11, 12, 13],
                          ['ViewExacqVisionOverwatchVMSConnections'],
                        ) && <Route path="/vms" element={<ExacqVisionVMSRoute />} />}

                        {perms.maintenanceLevel > 0 && (
                          <Route
                            path="/maintenancedashboard"
                            element={
                              <PageContentWrapper>
                                <DeviceMaintenanceRoute />
                              </PageContentWrapper>
                            }
                          />
                        )}

                        {routes.length > 0 && pagesLoaded && (
                          <>
                            <Route path="*" element={<Page404 />} />
                          </>
                        )}
                        {routes.length > 0 &&
                          routes
                            .filter((f) => f.page && f.permission())
                            .map((route, index) => (
                              <Route
                                key={index}
                                path={route.href}
                                element={<PageContentWrapper>{route.page}</PageContentWrapper>}
                              />
                            ))}
                      </>
                    )}
                  </Routes>
                </div>
              </div>
            </div>
          </Router>
        </>
      )}
      <ToastContainer
        draggable // Allow drag to dismiss
        autoClose={3000} // 3 seconds auto close
        pauseOnHover // Pause timer on hover
        position={dimensions.width > 768 ? 'top-right' : 'top-center'}
        toastStyle={{ width: 'min(90dvw, 400px)', marginTop: '10px' }}
        theme="colored"
      />
    </>
  );
};

const PageContentWrapper = ({ children, allowPageScroll = false }) => {
  return (
    <div
      className={`page-content-wrapper d-flex flex-column text-white h-100 w-100 customScrollbar-X customScrollbar-Y bg-base p-0 p-md-3 rounded-0 rounded-md-3 ${
        allowPageScroll ? 'page-content-scrollable' : ''
      }`}
    >
      {children}
    </div>
  );
};

const ContextProvider = ({
  children,
  colors,
  sidebarOpen,
  layoutObject,
  microsoftContextValue,
  setMicrosoftContextValue,
  uwfValues,
  setUwfValues,
  themes,
  setColors,
  userAgent,
}) => {
  return (
    <ConfigProvider theme={{ token: { colorPrimary: colors.primaryShadow } }}>
      <MicrosoftContext.Provider
        value={{
          get: () => microsoftContextValue,
          set: setMicrosoftContextValue,
        }}
      >
        <UserAgentContext.Provider value={userAgent}>
          <SidebarContext.Provider value={sidebarOpen}>
            <LayoutSizeContext.Provider value={layoutObject}>
              <UniqueWidgetRefreshContext.Provider value={uwfValues}>
                <SetUniqueWidgetRefreshContext.Provider value={setUwfValues}>
                  <ColorContext.Provider value={colors}>
                    <SetColorContext.Provider value={setColors}>
                      <PermsProvider>
                        <ThemesListContext.Provider value={themes}>
                          <DeviceContextProvider>
                            {children}
                            {DevRenderOverlay && <DevRenderOverlay />}
                          </DeviceContextProvider>
                        </ThemesListContext.Provider>
                      </PermsProvider>
                    </SetColorContext.Provider>
                  </ColorContext.Provider>
                </SetUniqueWidgetRefreshContext.Provider>
              </UniqueWidgetRefreshContext.Provider>
            </LayoutSizeContext.Provider>
          </SidebarContext.Provider>
        </UserAgentContext.Provider>
      </MicrosoftContext.Provider>
    </ConfigProvider>
  );
};
